import { useState } from 'react';
import {
  Building2,
  Users,
  LayoutDashboard,
  Kanban,
  CheckSquare,
  Activity,
  Zap,
  Mail,
  BarChart3,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ActivitiesView,
  AutomationsView,
  CompaniesView,
  ContactsView,
  DashboardView,
  DealsView,
  EmailTemplatesView,
  ReportsView,
  TasksView,
} from '../components';

type LeadsTab =
  | 'dashboard'
  | 'contacts'
  | 'companies'
  | 'deals'
  | 'tasks'
  | 'activities'
  | 'automations'
  | 'templates'
  | 'reports';

const tabs: Array<{ value: LeadsTab; label: string; icon: React.ElementType; disabled?: boolean }> =
  [
    { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { value: 'contacts', label: 'Contacts', icon: Users },
    { value: 'companies', label: 'Companies', icon: Building2 },
    { value: 'deals', label: 'Deals', icon: Kanban },
    { value: 'tasks', label: 'Tasks', icon: CheckSquare },
    { value: 'activities', label: 'Activities', icon: Activity },
    { value: 'automations', label: 'Automations', icon: Zap },
    { value: 'templates', label: 'Templates', icon: Mail },
    { value: 'reports', label: 'Reports', icon: BarChart3 },
  ];

export function LeadsPage() {
  const [activeTab, setActiveTab] = useState<LeadsTab>('dashboard');

  return (
    <div className="flex flex-col h-full">
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as LeadsTab)}
        className="flex-1 flex flex-col"
      >
        <div className="border-b px-6">
          <TabsList className="h-10 bg-transparent p-0 gap-4">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                disabled={tab.disabled}
                className="h-10 rounded-none border-b-2 border-transparent px-1 pb-3 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
                {tab.disabled && (
                  <span className="ml-1.5 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/*
          Render only the active tab's panel so we don't mount eight
          panels worth of queries on first page load. The Dashboard
          panel alone issues 8 parallel Leads queries — paying that cost
          every time a user switches into the arche, regardless of which
          tab they actually want, is unnecessary. Active-only mount is
          the standard pattern for heavy tabbed surfaces.
        */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'dashboard' && (
            <TabsContent value="dashboard" className="mt-0 h-full" forceMount>
              <DashboardView />
            </TabsContent>
          )}
          {activeTab === 'contacts' && (
            <TabsContent value="contacts" className="mt-0 h-full" forceMount>
              <ContactsView />
            </TabsContent>
          )}
          {activeTab === 'companies' && (
            <TabsContent value="companies" className="mt-0 h-full" forceMount>
              <CompaniesView />
            </TabsContent>
          )}
          {activeTab === 'deals' && (
            <TabsContent value="deals" className="mt-0 h-full" forceMount>
              <DealsView />
            </TabsContent>
          )}
          {activeTab === 'tasks' && (
            <TabsContent value="tasks" className="mt-0 h-full" forceMount>
              <TasksView />
            </TabsContent>
          )}
          {activeTab === 'activities' && (
            <TabsContent value="activities" className="mt-0 h-full" forceMount>
              <ActivitiesView />
            </TabsContent>
          )}
          {activeTab === 'automations' && (
            <TabsContent value="automations" className="mt-0 h-full" forceMount>
              <AutomationsView />
            </TabsContent>
          )}
          {activeTab === 'templates' && (
            <TabsContent value="templates" className="mt-0 h-full" forceMount>
              <EmailTemplatesView />
            </TabsContent>
          )}
          {activeTab === 'reports' && (
            <TabsContent value="reports" className="mt-0 h-full" forceMount>
              <ReportsView />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
}
